import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useRef, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { Loader2, AlertCircle, CheckCircle2 } from 'lucide-react';
import { toast } from 'sonner';

import { Button } from '@/components/atoms/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/atoms/card';
import { Skeleton } from '@/components/atoms/skeleton';
import { Separator } from '@/components/atoms/separator';
import { PayabliPublicCheckout } from '@/components/organisms/payments/PayabliPublicCheckout';
import { PublicCenteredPage } from '@/components/public/PublicCenteredPage';
import { useFollowIntoView } from '@/components/public/useFollowIntoView';
import { publicPost } from '@/lib/api/publicClient';
import { publicPaymentEndpoints } from '@/lib/api/endpoints';
import { isTipOverLimit, MAX_TIP_DOLLARS, TIP_LIMIT_MESSAGE } from '@/lib/payments/tipLimit';

import { useTipPageData, tipPageKeys } from '@/hooks/payment/useTipPageData';
import type { TipPageData } from '@/types/onlineBooking';

export const Route = createFileRoute('/(public)/tip-option/$id')({
  component: TipOptionPage,
});

// ─── Tip option card ──────────────────────────────────────────────────────────

function TipOptionCard({
  label,
  amount,
  currency,
  selected,
  onSelect,
}: {
  label: string;
  amount: number;
  currency: string;
  selected: boolean;
  onSelect: () => void;
}) {
  const formatted = new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: currency.toUpperCase(),
  }).format(amount / 100);

  return (
    <button
      type="button"
      onClick={onSelect}
      className={[
        'w-full rounded-xl border-2 p-4 text-left transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary',
        selected
          ? 'border-primary bg-primary/5'
          : 'border-border hover:border-primary/50 hover:bg-muted/40',
      ].join(' ')}
    >
      <div className="flex items-center justify-between">
        <span className="font-semibold text-foreground">{label}</span>
        <span
          className={['text-lg font-bold', selected ? 'text-primary' : 'text-foreground'].join(' ')}
        >
          {amount === 0 ? 'No tip' : formatted}
        </span>
      </div>
    </button>
  );
}

// ─── Page ─────────────────────────────────────────────────────────────────────

/** Exported for tests: the file route only mounts it. */
export function TipOptionPage() {
  const { id } = Route.useParams();
  const navigate = useNavigate();

  const [selectedPercent, setSelectedPercent] = useState<number | 'custom' | null>(null);
  const [customDollars, setCustomDollars] = useState('');
  const [isProceeding, setIsProceeding] = useState(false);
  // Payabli captures the card on this page instead of redirecting to a hosted
  // checkout, so the page owns the "entering card" and "done" states itself.
  const [payabliOpen, setPayabliOpen] = useState(false);
  // Distinguish settled from merely accepted: a Payabli tip can go through ACH,
  // and telling someone their tip "has been received" before it clears
  // overstates what actually happened.
  const [tipOutcome, setTipOutcome] = useState<'SUCCEEDED' | 'PENDING' | null>(null);

  const queryClient = useQueryClient();
  const { data, isLoading, isError } = useTipPageData(id, {
    // A PENDING (ACH) tip is settled by a webhook this page never sees — poll
    // while the payload reports an attempt still clearing (SERVER-derived, so
    // a reload resumes the poll too) until it lands or terminally fails.
    refetchInterval: (query) => {
      const current = query.state.data as TipPageData | undefined;
      const settling =
        current?.tipAttempt?.status?.toUpperCase() === 'PENDING' && current?.alreadyTipped !== true;
      return settling ? 4000 : false;
    },
  });
  // Only a failure that follows an observed settlement gets the failure card;
  // a cold load after an old failed attempt is just the tip picker again.
  const sawSettlingRef = useRef(false);

  // Revealing the embedded card form replaces the button the payer just
  // clicked and grows the card by roughly a viewport, leaving its submit button
  // below the fold. The page scrolls with the document (PublicCenteredPage), so
  // keep the form's bottom in view — the component mounts its iframe
  // asynchronously, so this has to follow the growth, not scroll once.
  const checkoutRef = useRef<HTMLDivElement | null>(null);
  useFollowIntoView(checkoutRef, payabliOpen);

  if (isLoading) {
    return (
      <PublicCenteredPage>
        <Card className="w-full max-w-sm">
          <CardHeader>
            <Skeleton className="h-7 w-36" />
            <Skeleton className="h-4 w-52 mt-1" />
          </CardHeader>
          <CardContent className="space-y-3">
            {Array.from({ length: 4 }).map((_, i) => (
              <Skeleton key={i} className="h-16 w-full rounded-xl" />
            ))}
            <Skeleton className="h-10 w-full" />
          </CardContent>
        </Card>
      </PublicCenteredPage>
    );
  }

  if (isError || !data) {
    return (
      <PublicCenteredPage>
        <Card className="w-full max-w-sm text-center p-8">
          <AlertCircle className="h-12 w-12 text-destructive mx-auto mb-4" />
          <h2 className="text-lg font-semibold">Page not found</h2>
          <p className="text-muted-foreground text-sm mt-2">
            This tip page may be expired or invalid.
          </p>
        </Card>
      </PublicCenteredPage>
    );
  }

  const pageData = data;
  // Server-derived ACH settlement state: the charge handler refreshes this
  // payload before reporting PENDING, and a reload lands back here through
  // `tipAttempt` alone — no tab-local flag survives a refresh, so none gates.
  const serverTipSettling =
    pageData.tipAttempt?.status?.toUpperCase() === 'PENDING' && pageData.alreadyTipped !== true;
  if (serverTipSettling || tipOutcome === 'PENDING') {
    sawSettlingRef.current = true;
  }
  const settled =
    tipOutcome === 'SUCCEEDED' || (sawSettlingRef.current && pageData.alreadyTipped === true);
  const settling = !settled && (serverTipSettling || tipOutcome === 'PENDING');
  const failedNow =
    !settled &&
    !settling &&
    sawSettlingRef.current &&
    pageData.tipAttempt?.status?.toUpperCase() === 'FAILED';
  if (settled || settling || failedNow) {
    const failed = failedNow;
    if (failed) {
      return (
        <PublicCenteredPage>
          <Card className="w-full max-w-sm text-center">
            <CardHeader>
              <AlertCircle className="h-10 w-10 text-destructive mx-auto" />
              <CardTitle>Tip payment failed</CardTitle>
              <CardDescription>
                Your bank transfer could not be completed. No tip was collected — you can try again
                with another payment method.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-3">
              <Button
                className="w-full"
                onClick={() => {
                  // Clearing the watch flag too: the FAILED attempt is now
                  // acknowledged, so the picker renders instead of this card
                  // bouncing straight back.
                  sawSettlingRef.current = false;
                  setTipOutcome(null);
                  setPayabliOpen(false);
                }}
              >
                Try again
              </Button>
              <Button asChild variant="outline" className="w-full">
                <a href={pageData.returnUrl ?? `/invoice/${id}`}>Back to invoice</a>
              </Button>
            </CardContent>
          </Card>
        </PublicCenteredPage>
      );
    }
    return (
      <PublicCenteredPage>
        <Card className="w-full max-w-sm text-center">
          <CardHeader>
            {settled ? (
              <CheckCircle2 className="h-10 w-10 text-green-600 mx-auto" />
            ) : (
              <Loader2 className="h-10 w-10 text-muted-foreground mx-auto" />
            )}
            <CardTitle>{settled ? 'Thank you!' : 'Tip submitted'}</CardTitle>
            <CardDescription>
              {settled
                ? 'Your tip has been received.'
                : 'Your tip is being processed and will show on the invoice once it clears.'}
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Button asChild className="w-full">
              <a href={pageData.returnUrl ?? `/invoice/${id}`}>Back to invoice</a>
            </Button>
          </CardContent>
        </Card>
      </PublicCenteredPage>
    );
  }
  if (pageData.alreadyTipped) {
    return (
      <PublicCenteredPage>
        <Card className="w-full max-w-sm text-center">
          <CardHeader>
            <CardTitle>Tip already added</CardTitle>
            <CardDescription>This invoice already has a gratuity attached.</CardDescription>
          </CardHeader>
          <CardContent>
            <Button asChild className="w-full">
              <a href={pageData.returnUrl ?? `/invoice/${id}`}>Back to invoice</a>
            </Button>
          </CardContent>
        </Card>
      </PublicCenteredPage>
    );
  }
  const baseAmount = pageData.baseAmount;
  const currency = pageData.currency;
  // Taken as sent. An empty list is a deliberate "custom amount only" from the
  // tenant's billing settings, not a signal to substitute the defaults.
  const tipOptions = pageData.tipOptions ?? [];

  const activePercent: number | 'custom' = selectedPercent ?? tipOptions[0]?.percent ?? 0;

  function tipCentsForPercent(pct: number) {
    return Math.round((baseAmount * pct) / 100);
  }

  const selectedTipCents =
    activePercent === 'custom'
      ? Math.round((parseFloat(customDollars || '0') || 0) * 100)
      : tipCentsForPercent(activePercent);

  // The invoice has already been paid by the time the tip page is reachable
  // (the backend rejects tip collection while a balance is due). Only the tip
  // is charged today — never re-charge the service total.
  const totalCents = selectedTipCents;
  // The server refuses a tip over the cap; the alert below explains why.
  const tipOverLimit = isTipOverLimit(selectedTipCents);

  function formatCurrency(cents: number) {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency.toUpperCase(),
    }).format(cents / 100);
  }

  async function handleProceed() {
    if (selectedTipCents <= 0) {
      setIsProceeding(true);
      await navigate({
        to: '/tip/$id',
        params: { id },
        search: { status: 'cancel' },
      });
      setIsProceeding(false);
      return;
    }
    if (tipOverLimit) {
      toast.error(TIP_LIMIT_MESSAGE);
      return;
    }
    // Branch on the processor EXPLICITLY — never fall through to a Stripe
    // session on an unknown value. Payabli has no hosted checkout: reveal the
    // embedded component in place and charge from here.
    if (pageData.processor === 'payabli') {
      setPayabliOpen(true);
      return;
    }
    setIsProceeding(true);
    try {
      const data = await publicPost<{ url?: string }>(publicPaymentEndpoints.createTipSession(id), {
        tipCents: selectedTipCents,
      });
      if (data.url) {
        window.location.href = data.url;
        return;
      }
      throw new Error('We couldn’t open the secure checkout. Please try again.');
    } catch {
      await navigate({
        to: '/tip/$id',
        params: { id },
        search: { status: 'cancel' },
      });
    } finally {
      setIsProceeding(false);
    }
  }

  const isPayabli = pageData.processor === 'payabli';
  const payabliConfig = pageData.payabli ?? null;
  // A processor we can't render a checkout for. `processor` is absent on a
  // payload cached before the field existed, when Stripe was the only option.
  const tippingUnavailable =
    (isPayabli && !payabliConfig) ||
    (!isPayabli && Boolean(pageData.processor) && pageData.processor !== 'stripe');

  return (
    <PublicCenteredPage>
      {/* Branding */}
      <header className="mb-6 text-center">
        {pageData.companyLogoUrl ? (
          <img
            src={pageData.companyLogoUrl}
            alt={pageData.companyName}
            className="h-12 mx-auto object-contain mb-2"
            loading="lazy"
          />
        ) : pageData.companyName ? (
          <h1 className="text-xl font-bold text-foreground">{pageData.companyName}</h1>
        ) : null}
      </header>

      <Card className="w-full max-w-sm">
        <CardHeader>
          <CardTitle>Add a Tip</CardTitle>
          <CardDescription>
            Your tip is greatly appreciated and goes directly to your service provider.
          </CardDescription>
        </CardHeader>

        <CardContent className="space-y-3">
          {/* Amount summary */}
          <div className="rounded-lg bg-muted/40 border border-border p-3 flex items-center justify-between text-sm mb-4">
            <span className="text-muted-foreground">
              {pageData.invoiceNumber ? `Invoice ${pageData.invoiceNumber}` : 'Service total'}
            </span>
            <span className="font-semibold">{formatCurrency(baseAmount)}</span>
          </div>

          {/* Tip option cards */}
          {tipOptions.map((opt) => (
            <TipOptionCard
              key={opt.percent}
              label={`${opt.percent}%`}
              amount={tipCentsForPercent(opt.percent)}
              currency={currency}
              selected={activePercent === opt.percent}
              onSelect={() => {
                setSelectedPercent(opt.percent);
                setCustomDollars('');
              }}
            />
          ))}

          {/* No tip */}
          <TipOptionCard
            label="No tip"
            amount={0}
            currency={currency}
            selected={activePercent === 0}
            onSelect={() => {
              setSelectedPercent(0);
              setCustomDollars('');
            }}
          />

          {/* Custom amount */}
          <div
            className={[
              'w-full rounded-xl border-2 p-4 transition-all',
              activePercent === 'custom' ? 'border-primary bg-primary/5' : 'border-border',
            ].join(' ')}
          >
            <p className="text-sm font-medium mb-2 text-foreground">Custom amount</p>
            <div className="flex items-center gap-2">
              <span className="text-muted-foreground">$</span>
              <input
                type="number"
                min="0"
                max={MAX_TIP_DOLLARS}
                step="0.01"
                value={customDollars}
                onChange={(e) => {
                  setCustomDollars(e.target.value);
                  setSelectedPercent('custom');
                }}
                placeholder="0.00"
                className="flex-1 rounded-lg border border-border px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary bg-background"
              />
            </div>
          </div>

          {/* Shown, never clamped: a percent option on a large total can pass
              the cap too, and silently changing the customer's figure is worse. */}
          {tipOverLimit && (
            <p role="alert" className="text-sm text-destructive">
              {TIP_LIMIT_MESSAGE}
            </p>
          )}

          {/* Total */}
          {selectedTipCents > 0 && (
            <div className="rounded-lg bg-muted/40 border border-border p-3 space-y-1 text-sm">
              <div className="flex justify-between text-muted-foreground">
                <span>Service (already paid)</span>
                <span>{formatCurrency(baseAmount)}</span>
              </div>
              <div className="flex justify-between text-muted-foreground">
                <span>Tip</span>
                <span>{formatCurrency(selectedTipCents)}</span>
              </div>
              <Separator className="my-1" />
              <div className="flex justify-between font-semibold text-foreground">
                <span>Total due today</span>
                <span>{formatCurrency(totalCents)}</span>
              </div>
            </div>
          )}

          {tippingUnavailable && selectedTipCents > 0 ? (
            <p className="pt-2 text-center text-sm text-muted-foreground">
              Online tipping is temporarily unavailable. Please contact the business.
            </p>
          ) : isPayabli && payabliOpen && payabliConfig && !tipOverLimit ? (
            <div className="pt-2" ref={checkoutRef}>
              <PayabliPublicCheckout
                config={payabliConfig}
                submitLabel={`Tip ${formatCurrency(selectedTipCents)}`}
                onCancel={() => setPayabliOpen(false)}
                onCharge={async (temporaryToken) => {
                  try {
                    const charge = await publicPost<{ status?: string }>(
                      publicPaymentEndpoints.tipPayabliCharge(id),
                      { tipCents: selectedTipCents, temporaryToken },
                    );
                    // Trust the settled status, not the 200 — a terminal
                    // non-success must not render as a thank-you, and an
                    // unsettled one must not claim the tip was received.
                    const status = charge?.status?.toUpperCase();
                    if (status !== 'SUCCEEDED' && status !== 'PENDING') {
                      throw new Error('Your tip could not be processed. Please try another card.');
                    }
                    if (status === 'PENDING') {
                      // Refresh BEFORE reporting pending: the settling state
                      // (and its poll) derives from the payload's tipAttempt,
                      // which this fetch makes current.
                      await queryClient.invalidateQueries({
                        queryKey: tipPageKeys.detail(id),
                      });
                    }
                    setTipOutcome(status);
                    toast.success(
                      status === 'SUCCEEDED' ? 'Thank you for the tip!' : 'Tip submitted.',
                    );
                  } catch (error) {
                    const message =
                      error instanceof Error && error.message
                        ? error.message
                        : 'Your tip could not be processed. Please try again.';
                    toast.error(message);
                    // Rethrow so the embedded form re-enables and shows its error.
                    throw error;
                  }
                }}
              />
            </div>
          ) : (
            <Button
              type="button"
              className="w-full mt-2"
              onClick={handleProceed}
              disabled={isProceeding || tipOverLimit}
            >
              {isProceeding && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              {selectedTipCents > 0
                ? `Continue — ${formatCurrency(totalCents)}`
                : 'Continue without tip'}
            </Button>
          )}
        </CardContent>
      </Card>
    </PublicCenteredPage>
  );
}
